import type { StaticImageData } from 'next/image';
import type { ReactNode } from 'react';
import Image from 'next/image';
import { Icon } from '@/components/entities/Sprite';
import { cn } from '@/utils/cn';

interface Props {
  isActive: boolean;
  iconName: 'gold-coin' | 'sweep-coin';
  shineSrc: StaticImageData;
  shineAlt: string;
  shineIdlePosition: string;
  shineActivePosition: string;
  transitionDuration: string;
  label: string;
  labelSuffix?: ReactNode;
  value: string;
  onSelect: () => void;
}

const CurrencyOptionButton = ({
  isActive,
  iconName,
  shineSrc,
  shineAlt,
  shineIdlePosition,
  shineActivePosition,
  transitionDuration,
  label,
  labelSuffix,
  value,
  onSelect,
}: Props) => {
  return (
    <button
      className={cn(
        'group flex items-center justify-between w-full gap-2 pl-2.5 pr-4 py-2 rounded-[3.125rem] relative transition-all',
        transitionDuration,
        isActive && 'glass-effect',
      )}
      type="button"
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'relative shrink-0 flex items-center justify-center overflow-visible',
            isActive ? 'size-11' : 'size-8',
          )}
        >
          <Image
            src={shineSrc}
            alt={shineAlt}
            width={48}
            height={48}
            aria-hidden
            className={cn(
              'pointer-events-none -z-1 absolute inset-0 m-auto size-11 object-cover transition-opacity opacity-0 group-hover:opacity-100',
              transitionDuration,
              shineIdlePosition,
              isActive && ['opacity-100', shineActivePosition],
            )}
          />
          <Icon name={iconName} className={cn('relative z-10', isActive ? 'w-11 h-[2.688rem]' : 'size-8')} />
        </div>
        <div className="flex flex-col items-start gap-0.5">
          <div className="flex items-center gap-1">
            <span className={cn('text-regular text-[0.75rem] leading-4', isActive ? 'text-white' : 'text-c300')}>
              {label}
            </span>
            {labelSuffix}
          </div>
          <span className={cn('text-xl font-medium leading-[130%]', isActive ? 'text-white' : 'text-c300')}>
            {value}
          </span>
        </div>
      </div>
      <Icon name="check-main" className={cn('size-4', isActive ? 'visible' : 'invisible')} />
    </button>
  );
};

export default CurrencyOptionButton;
